import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, LogOut, Sparkles, Check, Calendar, CreditCard, ExternalLink, Crown, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PremiumPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const PACKAGES = [
    {
      id: "monthly",
      name: t('premium.monthly'),
      price: 9.99,
      period: t('premium.perMonth'),
      features: [
        t('premium.features.unlimited'),
        t('premium.features.artwork'),
        t('premium.features.circles'),
        t('premium.features.priority'),
        t('premium.features.updates')
      ],
      popular: false
    },
    {
      id: "yearly",
      name: t('premium.yearly') || 'Vuositilaus',
      price: 79.90,
      period: t('premium.perYear') || '/ vuosi',
      savings: t('premium.yearlySavings') || 'Säästä 33%',
      features: [
        t('premium.features.unlimited'),
        t('premium.features.artwork'),
        t('premium.features.circles'),
        t('premium.features.priority'),
        t('premium.features.bestValue')
      ],
      popular: true
    }
  ];

  const handleUpgrade = async (packageId) => {
    setLoading(packageId);
    
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API}/payments/checkout?package_id=${packageId}`,
        { origin_url: originUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
      setLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const returnUrl = window.location.href;
      
      const response = await axios.post(
        `${API}/payments/customer-portal`,
        { return_url: returnUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Customer Portal
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('common.error');
      toast.error(errorMessage);
      setPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
                <img src="/logo-new.png" alt="Uneksia" className="w-12 h-12" />
                <span className="text-2xl font-heading font-light text-void tracking-tight">{t('appName')}</span>
              </div>
              <div className="hidden md:flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dashboard"
                >
                  {t('nav.dashboard')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dreams')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-dreams"
                >
                  {t('nav.myDreams')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/community-hub')}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-community-hub"
                >
                  {t('nav.community')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/blog")}
                  className="font-body text-void hover:text-void/80"
                  data-testid="nav-blog"
                >
                  {t("blog.title")}
                </Button>
                {user?.is_admin && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/admin')}
                    className="font-body text-red-500 hover:text-red-600 font-medium"
                    data-testid="nav-admin"
                  >
                    Admin
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector variant="minimal" />
              <Button
                variant="ghost"
                onClick={() => navigate('/settings')}
                className="text-void hover:text-void/80"
                data-testid="settings-button"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={onLogout}
                className="text-void hover:text-void/80"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Already Premium Section */}
        {user?.is_premium && (
          <div className="max-w-2xl mx-auto mb-16">
            <div className="glass-effect rounded-3xl p-8 text-center ring-2 ring-lucid">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-lucid/30 rounded-full mb-6">
                <Sparkles className="w-5 h-5 text-void" />
                <span className="text-sm font-body text-void font-medium">
                  {user?.premium_type === 'yearly' 
                    ? (t('premium.yearlyBadge') || 'Vuositilaus Premium')
                    : (t('premium.activeBadge') || 'Premium Aktiivinen')
                  }
                </span>
              </div>
              
              <h2 className="text-2xl font-heading font-light text-void mb-4">
                {t('premium.alreadyPremium') || 'Olet jo Premium-jäsen!'}
              </h2>
              
              {user?.premium_type === 'yearly' ? (
                <>
                  <p className="text-void/70 font-body mb-8">
                    {t('premium.yearlyDescription') || 'Sinulla on vuositilaus Premium-jäsenyys. Nauti rajattomista unianalyyseistä!'}
                  </p>
                  <div className="inline-flex items-center gap-2 px-6 py-3 bg-lucid/20 rounded-full">
                    <Crown className="w-5 h-5 text-void" />
                    <span className="font-body text-void font-medium">
                      {t('premium.yearlyStatus') || 'Vuositilaus aktiivinen'}
                    </span>
                  </div>
                </>
              ) : user?.premium_type === 'monthly' ? (
                <>
                  <p className="text-void/70 font-body mb-6">
                    {t('premium.manageDescription') || 'Voit hallita tilaustasi, nähdä maksuhistoriasi tai irtisanoa tilauksesi alla olevasta linkistä.'}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
                    <Button
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                      className="bg-void text-white hover:bg-void/90 rounded-full h-12 px-8 font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95 inline-flex items-center gap-2"
                      data-testid="manage-subscription-button"
                    >
                      <CreditCard className="w-4 h-4" />
                      {portalLoading ? t('common.loading') : (t('premium.manageSubscription') || 'Hallitse tilausta')}
                      <ExternalLink className="w-4 h-4" />
                    </Button>

                    <Button
                      onClick={() => handleUpgrade('yearly')}
                      disabled={loading === 'yearly'}
                      variant="outline"
                      className="rounded-full h-12 px-8 font-body font-medium border-lucid text-void hover:bg-lucid/10 transition-all duration-300 inline-flex items-center gap-2"
                      data-testid="upgrade-to-yearly-button"
                    >
                      <Calendar className="w-4 h-4" />
                      {loading === 'yearly' ? t('common.loading') : (t('premium.upgradeToYearly') || 'Päivitä vuositilaukseen — Säästä 33%')}
                    </Button>
                  </div>
                  
                  <p className="text-void/50 font-body text-sm">
                    {t('premium.stripePortalNote') || 'Sinut ohjataan Stripen turvalliseen hallintapaneeliin'}
                  </p>
                </>
              ) : (
                <p className="text-void/70 font-body">
                  {t('premium.enjoyDescription') || 'Nauti rajattomista unianalyyseistä ja premium-ominaisuuksista!'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Header - only show for non-premium */}
        {!user?.is_premium && (
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-ethereal/20 backdrop-blur-md rounded-full border border-ethereal/30 mb-6">
            <Sparkles className="w-5 h-5 text-void" />
            <span className="text-sm font-body text-void font-medium">{t('premium.badge')}</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-light text-void tracking-tight mb-4">
            {t('premium.title')} <span className="italic">{t('premium.titleItalic')}</span>
          </h1>
          
          <p className="text-lg text-void/70 font-body tracking-wide max-w-2xl mx-auto mb-2">
            {t('premium.subtitle')}
          </p>
        </div>
        )}

        {/* Pricing Cards - only show for non-premium */}
        {!user?.is_premium && (
        <>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {PACKAGES.map((pkg) => (
            <div
              key={pkg.id}
              className={`glass-effect rounded-3xl p-8 hover:shadow-lg transition-all duration-500 relative ${
                pkg.popular ? 'ring-2 ring-lucid' : ''
              }`}
              data-testid={`package-${pkg.id}`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-lucid text-void px-4 py-1 rounded-full text-sm font-body font-medium">
                    {t('premium.popular')}
                  </div>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-heading font-light text-void mb-2">{pkg.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-heading font-light text-void">{pkg.price.toFixed(2).replace('.', ',')} €</span>
                  <span className="text-void/60 font-body text-sm">{pkg.period}</span>
                </div>
                {pkg.savings && (
                  <span className="inline-block mt-2 px-3 py-1 bg-lucid/20 text-void text-xs font-body font-medium rounded-full">
                    {pkg.savings}
                  </span>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-lucid/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-void" />
                    </div>
                    <span className="text-void/80 font-body">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleUpgrade(pkg.id)}
                disabled={loading === pkg.id}
                className="w-full bg-void text-white hover:bg-void/90 rounded-full h-12 font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95"
                data-testid={`upgrade-${pkg.id}-button`}
              >
                {loading === pkg.id ? t('common.loading') : t('premium.subscribe')}
              </Button>
            </div>
          ))}
        </div>
        {/* Free Features */}
        <div className="max-w-4xl mx-auto mt-12 mb-4">
          <div className="glass-effect rounded-3xl p-8">
            <h3 className="text-xl font-heading font-light text-void mb-4 text-center">
              {t('premium.freeTitle') || "Ilmaiseksi kaikille"}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className="space-y-1">
                <div className="w-8 h-8 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-sm text-void/70 font-body">{t('premium.free.journal') || "Unipäiväkirja"}</p>
              </div>
              <div className="space-y-1">
                <div className="w-8 h-8 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-sm text-void/70 font-body">{t('premium.free.freeAnalyses') || "5 ilmaista analyysiä"}</p>
              </div>
              <div className="space-y-1">
                <div className="w-8 h-8 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-emerald-600" />
                </div>
                <p className="text-sm text-void/70 font-body">{t('premium.free.community') || "Yhteisö"}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Free trial notice + Payment methods */}
        <div className="max-w-4xl mx-auto mt-8 text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-50 border border-emerald-200 rounded-full">
            <Check className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-body text-void font-medium">
              {t('premium.freeTrial') || 'Vaivaton kokeilu ilman sitoutumista — ei pankkikorttia'}
            </span>
          </div>
          <p className="text-xs text-void/50 font-body">
            {t('premium.paymentMethods') || 'Maksutavat: Kortti, Klarna, Link'}
          </p>
        </div>
        </>
        )}
      </div>
    </div>
  );
}